import React, { useMemo } from 'react';

const formatDisplayValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (Array.isArray(value)) {
    const isCharArray = value.length > 0 && value.every(v => typeof v === 'string' && v.length === 1);
    return isCharArray ? value.join('') : value.join(', ');
  }
  return String(value);
};

const renderInvoiceField = (label, value, fullWidth = false) => (
  <div className={`document-field${fullWidth ? ' full-width' : ''}`}>
    <span className="document-label">{label}</span>
    <span className="document-value">{value}</span>
  </div>
);

const buildMatchedIndex = (detections) => {
  if (!Array.isArray(detections)) return null;
  const byOpInstance = new Set();
  const byOpInstancePage = new Set();
  const byOpPath = new Set();
  const byCategory = new Set();
  const byOpPathUnfiltered = new Set();
  detections.forEach((det) => {
    const overrideExtra = (det?.extras || []).find((e) => e?.label === '_match_override');
    if (overrideExtra) {
      const raw = overrideExtra.value;
      const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
      list.forEach((ov) => {
        if (ov?.op_path) {
          byOpPathUnfiltered.add(ov.op_path);
          byCategory.add(ov.op_path.split('.')[0]);
        }
      });
    }
    const matches = (det?.extras || []).find((e) => e?.label === '_matches')?.value;
    if (!Array.isArray(matches)) return;
    matches.forEach((m) => {
      if (!m?.op_path) return;
      byOpPath.add(m.op_path);
      const category = m.op_path.split('.')[0];
      byCategory.add(category);
      if (m._provisional) return;
      if (typeof m.instance_idx === 'number') {
        byOpInstance.add(`${m.op_path}:${m.instance_idx}`);
        if (typeof m.page === 'number') {
          byOpInstancePage.add(`${m.op_path}:${m.page}:${m.instance_idx}`);
        }
      }
    });
  });
  return { byOpInstance, byOpInstancePage, byOpPath, byCategory, byOpPathUnfiltered };
};

const AnalysisOperations = ({
  analysis,
  selectedPage,
  onSelectPage,
  showPageSelector = true,
  forceInvoiceView = false,
  hideInvoiceFallback = false,
  operationsEnabled = null,
  detections = null,
}) => {
  const result = analysis.result;
  const fullJson = result?.full_json || result || {};
  const operaciones = fullJson.OperacionesMecanizado || {};
  const invoiceData = fullJson._invoice_data || null;
  const pageInfo = fullJson._page_info || {};
  const drawingPageNumbers = pageInfo.drawing_page_numbers || [];
  const hasMultipleDrawings = drawingPageNumbers.length > 1;

  const matchedIndex = useMemo(() => buildMatchedIndex(detections), [detections]);
  const supportFilterEnabled = !!matchedIndex;

  const hasOperaciones = Object.keys(operaciones).some(key => {
    const data = operaciones[key];
    if (!data) return false;
    if (Array.isArray(data)) return data.length > 0;
    if (typeof data === 'object') {
      return Object.values(data).some(v => v !== null && v !== undefined && (!Array.isArray(v) || v.length > 0));
    }
    return true;
  });

  if (forceInvoiceView) {
    if (!invoiceData) {
      return (
        <div className="document-data-center">
          <h2>Información del documento</h2>
          <div className="no-data-message">
            <p>No se han encontrado datos de albarán o factura en el documento.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="document-data-center">
        <h2>Información del documento</h2>
        <div className="document-data-grid">
          {(invoiceData.numero_documento || invoiceData.numero_pedido) && renderInvoiceField('Nº Documento', invoiceData.numero_documento || invoiceData.numero_pedido)}
          {invoiceData.fecha && renderInvoiceField('Fecha', invoiceData.fecha)}
          {invoiceData.cliente && renderInvoiceField('Cliente', invoiceData.cliente)}
          {invoiceData.proveedor && renderInvoiceField('Proveedor', invoiceData.proveedor)}
          {invoiceData.articulo && renderInvoiceField('Artículo', invoiceData.articulo)}
          {invoiceData.descripcion && renderInvoiceField('Descripción', invoiceData.descripcion, true)}
          {invoiceData.cantidad && renderInvoiceField('Cantidad', invoiceData.cantidad)}
          {invoiceData.plazo_entrega && renderInvoiceField('Plazo entrega', invoiceData.plazo_entrega)}
          {invoiceData.peso_total_kg && renderInvoiceField('Peso total', `${invoiceData.peso_total_kg} kg`)}
          {invoiceData.importe_total && renderInvoiceField('Importe', `${invoiceData.importe_total}€`)}
          {invoiceData.notas && (
            <div className="document-field full-width">
              <span className="document-label">Notas</span>
              <span className="document-value notes">{invoiceData.notas}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (hideInvoiceFallback && !hasOperaciones) {
    const message = operationsEnabled === false
      ? 'Las operaciones de mecanizado no están activadas para esta cuenta.'
      : 'No se han detectado operaciones de mecanizado en este plano.';
    return (
      <div className="document-data-center">
        <h2>Operaciones de Mecanizado</h2>
        <div className="no-data-message">
          <p>{message}</p>
        </div>
      </div>
    );
  }

  if (!hasOperaciones && !invoiceData) {
    return (
      <div className="document-data-center">
        <h2>Análisis Completado</h2>
        <div className="no-data-message">
          <p>No se han encontrado datos de albarán o factura en el documento.</p>
          <p>El análisis queda registrado para detectar coincidencias con este plano en futuras búsquedas.</p>
        </div>
      </div>
    );
  }

  if (!hasOperaciones && invoiceData) {
    return (
      <div className="document-data-center">
        <h2>Datos del Documento</h2>
        <div className="document-data-grid">
          {(invoiceData.numero_documento || invoiceData.numero_pedido) && renderInvoiceField('Nº Documento', invoiceData.numero_documento || invoiceData.numero_pedido)}
          {invoiceData.fecha && renderInvoiceField('Fecha', invoiceData.fecha)}
          {invoiceData.cliente && renderInvoiceField('Cliente', invoiceData.cliente)}
          {invoiceData.proveedor && renderInvoiceField('Proveedor', invoiceData.proveedor)}
          {invoiceData.articulo && renderInvoiceField('Artículo', invoiceData.articulo)}
          {invoiceData.descripcion && renderInvoiceField('Descripción', invoiceData.descripcion, true)}
          {invoiceData.cantidad && renderInvoiceField('Cantidad', invoiceData.cantidad)}
          {invoiceData.plazo_entrega && renderInvoiceField('Plazo entrega', invoiceData.plazo_entrega)}
          {invoiceData.peso_total_kg && renderInvoiceField('Peso total', `${invoiceData.peso_total_kg} kg`)}
          {invoiceData.importe_total && renderInvoiceField('Importe', `${invoiceData.importe_total}€`)}
          {invoiceData.notas && (
            <div className="document-field full-width">
              <span className="document-label">Notas</span>
              <span className="document-value notes">{invoiceData.notas}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="categories-section">
      <h2>Operaciones de Mecanizado</h2>

      {showPageSelector && hasMultipleDrawings && (
        <div className="drawing-tabs">
          {drawingPageNumbers.map((pageNum) => (
            <button
              key={pageNum}
              className={`drawing-tab ${selectedPage === pageNum ? 'active' : ''}`}
              onClick={() => onSelectPage && onSelectPage(pageNum)}
            >
              Dibujo Pág. {pageNum}
            </button>
          ))}
        </div>
      )}

      {Object.entries(operaciones).map(([categoryName, categoryData]) => {
        if (Array.isArray(operationsEnabled) && operationsEnabled.length > 0 && !operationsEnabled.includes(categoryName)) {
          return null;
        }
        const categorySupported = !supportFilterEnabled || matchedIndex.byCategory.has(categoryName);
        if (!categorySupported) return null;

        const simpleFields = {};
        const arrayFields = {};

        if (Array.isArray(categoryData)) {
          arrayFields['items'] = { items: categoryData, opPath: categoryName };
        } else if (typeof categoryData === 'object' && categoryData !== null) {
          Object.entries(categoryData).forEach(([key, value]) => {
            if (key.startsWith('_')) return;
            if (Array.isArray(value)) {
              arrayFields[key] = { items: value, opPath: `${categoryName}.${key}` };
            } else if (typeof value === 'object' && value !== null) {
              Object.entries(value).forEach(([subKey, subValue]) => {
                if (!subKey.startsWith('_')) {
                  simpleFields[`${key}_${subKey}`] = subValue;
                }
              });
            } else {
              simpleFields[key] = value;
            }
          });
        }

        const filteredArrayFields = {};
        Object.entries(arrayFields).forEach(([arrayName, { items, opPath }]) => {
          const unfiltered = matchedIndex && matchedIndex.byOpPathUnfiltered.has(opPath);
          const filteredItems = items.filter((item, idx) => {
            if (selectedPage && item && typeof item._page_number === 'number' && item._page_number !== selectedPage) {
              return false;
            }
            if (supportFilterEnabled && !unfiltered && !matchedIndex.byOpInstance.has(`${opPath}:${idx}`)) {
              return false;
            }
            return true;
          });
          if (filteredItems.length > 0) {
            filteredArrayFields[arrayName] = filteredItems;
          }
        });

        const hasContent = Object.keys(simpleFields).length > 0 ||
          Object.values(filteredArrayFields).some(arr => arr && arr.length > 0);

        if (!hasContent) return null;

        return (
          <div key={categoryName} className="category-card">
            <div className="category-header">
              <h3>{categoryName}</h3>
            </div>

            {Object.keys(simpleFields).length > 0 && (
              <div className="category-general-info">
                <div className="info-grid">
                  {Object.entries(simpleFields).map(([key, value]) => (
                    <div key={key} className="info-field">
                      <span className="field-label">{key}:</span>
                      <span className="field-value">{formatDisplayValue(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Object.entries(filteredArrayFields).map(([arrayName, arrayItems]) => {
              if (!arrayItems || arrayItems.length === 0) return null;

              const allPrimitives = arrayItems.every(item => typeof item !== 'object' || item === null);

              if (allPrimitives) {
                return (
                  <div key={arrayName} className="category-array-section">
                    {arrayName !== 'items' && <h4>{arrayName}</h4>}
                    <div className="simple-array-values">
                      {arrayItems.map((item, idx) => (
                        <span key={idx} className="simple-value">{formatDisplayValue(item)}</span>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <div key={arrayName} className="category-array-section">
                  {arrayName !== 'items' && <h4>{arrayName}</h4>}
                  <div className="array-items-grid">
                    {arrayItems.map((item, idx) => {
                      const pageNum = item?._page_number;
                      return (
                        <div key={idx} className="array-item-card">
                          <div className="array-item-header">
                            Item {idx + 1}
                            {pageNum && !hasMultipleDrawings && <span className="item-page-badge">Pág. {pageNum}</span>}
                          </div>
                          <div className="array-item-details">
                            {Object.entries(item)
                              .filter(([itemKey]) => !itemKey.startsWith('_'))
                              .map(([itemKey, itemValue]) => (
                                <div key={itemKey} className="array-item-field">
                                  <span className="item-label">{itemKey}:</span>
                                  <span className="item-value">{formatDisplayValue(itemValue)}</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default AnalysisOperations;
